import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Popover from '@radix-ui/react-popover';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  ChevronDown,
  Eye,
  EyeOff,
  Loader2,
  MoreHorizontal,
  RotateCcw,
  Tag,
  Trash2,
  UserMinus,
  UserPlus,
  X,
} from 'lucide-react';
import { forwardRef, useEffect, useRef, useState, type ReactNode } from 'react';
import { post, type ApiError } from '../../lib/api';
import { cn } from '../../lib/cn';
import { pluralise } from '../../lib/format';
import { useAgents, useGroups } from '../../lib/directory';
import { useSession } from '../../lib/session';
import { Avatar, Badge, Button, Input, type ButtonProps } from '../../components/ui';
import { useConfirm } from '../../components/dialog';
import { useToast } from '../../components/toast';
import { invalidateTicket } from './list-cache';
import { restoreTickets } from './list-restore';
import type { StatusChoice } from './inline-status';

/**
 * The bar that appears when tickets are selected.
 *
 * Every change goes through `POST /tickets/bulk`, which applies it through the
 * ordinary single-ticket path — so SLA recalculation, automation and audit
 * behave exactly as they do for one ticket edited by hand.
 *
 * Tickets are addressed by *number*, the one people quote and the one the bulk
 * endpoint looks tickets up by. The selection used to hold internal ids, so
 * once ids and numbers drifted apart an action on #105 changed #107.
 *
 * It floats over the bottom of the results rather than being inserted above
 * them. Inserted, it pushed every row down by its own height the moment the
 * first box was ticked, so a quick second tick landed on the row above.
 *
 * Partial success is a normal outcome. The tickets that went through are let
 * go; the ones that did not stay selected, with the reason, so the next action
 * applies to exactly those — not, as before, to all fifteen again.
 */

interface BulkResult {
  data: {
    status: 'completed' | 'partial' | 'failed';
    succeeded: number;
    failed: number;
    failures: Array<{ number: number; reason: string }>;
  };
}

interface BulkRequest {
  numbers: number[];
  body: Record<string, unknown>;
  /** "Assigned 5 tickets to Sara" — said when everything went through. */
  done: (count: number) => string;
  /** What to call the failure: "updated", "moved to the trash"… */
  verb: string;
}

interface Choice {
  value: string;
  label: string;
}

/** The most tags one bulk request may carry; the server refuses more. */
const MAX_TAGS = 20;

export interface BulkActionBarProps {
  mode: 'active' | 'trash';
  /** Selected ticket numbers. */
  selected: number[];
  /** Numbers of the tickets on the current page, to say how many are elsewhere. */
  visibleNumbers: number[];
  onClear: () => void;
  onSelectOnly: (numbers: number[]) => void;
  statuses: StatusChoice[];
  priorities: Choice[];
}

export function BulkActionBar({
  mode,
  selected,
  visibleNumbers,
  onClear,
  onSelectOnly,
  statuses,
  priorities,
}: BulkActionBarProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { can, session } = useSession();
  // Kept with the selection it explains, so it disappears once that moves on.
  const [failures, setFailures] = useState<{ key: string; list: Array<{ number: number; reason: string }> } | null>(
    null,
  );
  const [undo, setUndo] = useState<number[] | null>(null);
  const undoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (undoTimer.current) clearTimeout(undoTimer.current);
    },
    [],
  );

  const dismissUndo = () => {
    if (undoTimer.current) clearTimeout(undoTimer.current);
    undoTimer.current = null;
    setUndo(null);
  };

  const offerUndo = (numbers: number[]) => {
    if (undoTimer.current) clearTimeout(undoTimer.current);
    setUndo(numbers);
    undoTimer.current = setTimeout(() => setUndo(null), 20_000);
  };

  const settle = (request: BulkRequest, result: BulkResult['data']) => {
    invalidateTicket(queryClient, request.numbers);
    const total = request.numbers.length;
    const isDelete = request.body.action === 'delete';

    if (isDelete) {
      const failed = new Set(result.failures.map((failure) => failure.number));
      const trashed = request.numbers.filter((number) => !failed.has(number));
      // The undo bar says what happened, with the way back beside it; a toast
      // as well would be the same news twice.
      if (trashed.length > 0) offerUndo(trashed);
    }

    if (result.failed === 0) {
      setFailures(null);
      onClear();
      if (!isDelete) toast.success(request.done(total));
      return;
    }
    const failedNumbers = result.failures.map((failure) => failure.number);
    setFailures({ key: selectionKeyOf(failedNumbers), list: result.failures });
    // Only the ones that need another look stay selected.
    onSelectOnly(failedNumbers);
    const first = result.failures[0];
    toast.error(
      result.succeeded === 0
        ? `None of the ${pluralise(total, 'ticket')} could be ${request.verb}`
        : `${result.failed} of ${pluralise(total, 'ticket')} couldn’t be ${request.verb}`,
      first ? `#${first.number}: ${first.reason}${result.failed > 1 ? ' — they are still selected.' : ''}` : undefined,
    );
  };

  const bulk = useMutation<BulkResult, ApiError, BulkRequest>({
    mutationFn: ({ numbers, body }) => post('/tickets/bulk', { numbers, ...body }),
    onSuccess: (response, request) => settle(request, response.data),
    onError: (error) => toast.error(error),
  });

  const restore = useMutation<Awaited<ReturnType<typeof restoreTickets>>, ApiError, { numbers: number[]; undo: boolean }>({
    mutationFn: ({ numbers }) => restoreTickets(numbers),
    onSuccess: (result, request) => {
      invalidateTicket(queryClient, request.numbers);
      if (request.undo) dismissUndo();
      if (result.failed.length === 0) {
        if (!request.undo) onClear();
        toast.success(`Restored ${pluralise(result.restored.length, 'ticket')}`);
        return;
      }
      if (!request.undo) onSelectOnly(result.failed.map((failure) => failure.number));
      const first = result.failed[0]!;
      toast.error(
        `${result.failed.length} of ${pluralise(request.numbers.length, 'ticket')} couldn’t be restored`,
        `#${first.number}: ${first.reason}`,
      );
    },
    onError: (error) => toast.error(error),
  });

  const shownFailures = failures && failures.key === selectionKeyOf(selected) ? failures.list : null;
  const count = selected.length;
  const visible = new Set(visibleNumbers);
  const hidden = selected.filter((number) => !visible.has(number)).length;
  const busy = bulk.isPending || restore.isPending;

  const run = (body: Record<string, unknown>, done: (n: number) => string, verb = 'updated') =>
    bulk.mutate({ numbers: [...selected], body, done, verb });

  const deleteSelected = async () => {
    const it = count === 1 ? 'it' : 'them';
    const ok = await confirm({
      title: `Move ${pluralise(count, 'ticket')} to the trash?`,
      description: `${count === 1 ? 'It disappears' : 'They disappear'} from every list${
        hidden > 0 ? `, including ${hidden} not on this page` : ''
      }. You can undo this straight away, and anyone who can delete tickets can restore ${it} from the Trash later.`,
      confirmLabel: `Move ${pluralise(count, 'ticket')} to trash`,
      tone: 'danger',
    });
    if (ok) run({ action: 'delete' }, (n) => `Moved ${pluralise(n, 'ticket')} to the trash`, 'moved to the trash');
  };

  if (count === 0) {
    if (!undo) return null;
    return (
      <Floating label="Undo">
        <span className="text-body text-ink" role="status">
          {pluralise(undo.length, 'ticket')} moved to the trash.
        </span>
        <Button
          size="sm"
          variant="primary"
          loading={restore.isPending}
          onClick={() => restore.mutate({ numbers: undo, undo: true })}
        >
          {!restore.isPending && <RotateCcw aria-hidden className="size-3" />}
          Undo
        </Button>
        <Button size="icon" variant="ghost" className="size-7" aria-label="Dismiss" onClick={dismissUndo}>
          <X aria-hidden className="size-3.5" />
        </Button>
      </Floating>
    );
  }

  const summary = busy ? (
    <span className="inline-flex items-center gap-2 text-body font-medium text-ink" role="status">
      <Loader2 aria-hidden className="size-3.5 animate-spin text-ink-subtle" />
      {restore.isPending ? `Restoring ${pluralise(count, 'ticket')}…` : `Updating ${pluralise(count, 'ticket')}…`}
    </span>
  ) : (
    <span className="text-body font-medium text-ink">
      {pluralise(count, 'ticket')} selected
      {hidden > 0 && <span className="font-normal text-ink-muted"> · {hidden} on other pages</span>}
    </span>
  );

  if (mode === 'trash') {
    return (
      <Floating label="Selected tickets">
        {summary}
        {can('ticket.delete') && (
          <Button
            size="sm"
            variant="primary"
            disabled={busy}
            onClick={() => restore.mutate({ numbers: [...selected], undo: false })}
          >
            <RotateCcw aria-hidden className="size-3" />
            Restore
          </Button>
        )}
        <ClearButton onClear={onClear} disabled={busy} />
      </Floating>
    );
  }

  const me = session?.user ?? null;

  return (
    <Floating label="Bulk actions">
      {summary}
      <span aria-hidden className="mx-0.5 h-5 w-px bg-line" />

      {can('ticket.assign') && (
        <AssignMenu
          disabled={busy}
          meId={me?.id ?? null}
          onAssign={(person) =>
            run({ action: 'update', changes: { assignee_id: person?.id ?? null } }, (n) =>
              person === null
                ? `Unassigned ${pluralise(n, 'ticket')}`
                : person.id === me?.id
                  ? `Assigned ${pluralise(n, 'ticket')} to you`
                  : `Assigned ${pluralise(n, 'ticket')} to ${person.name}`,
            )
          }
        />
      )}
      <GroupMenu
        disabled={busy}
        onPick={(group) =>
          run({ action: 'update', changes: { group_id: group?.id ?? null } }, (n) =>
            group ? `Moved ${pluralise(n, 'ticket')} to ${group.name}` : `Removed the group from ${pluralise(n, 'ticket')}`,
          )
        }
      />
      <ChoiceMenu
        label="Status"
        choices={statuses}
        disabled={busy}
        onPick={(choice) =>
          run({ action: 'update', changes: { status: choice.value } }, (n) => `Set ${pluralise(n, 'ticket')} to ${choice.label}`)
        }
      />
      <ChoiceMenu
        label="Priority"
        choices={priorities}
        disabled={busy}
        onPick={(choice) =>
          run(
            { action: 'update', changes: { priority: choice.value } },
            (n) => `Set ${pluralise(n, 'ticket')} to ${choice.label} priority`,
          )
        }
      />
      <TagsMenu
        disabled={busy}
        onApply={(action, tags) =>
          run({ action, tags }, (n) =>
            action === 'add_tags' ? `Tagged ${pluralise(n, 'ticket')}` : `Removed tags from ${pluralise(n, 'ticket')}`,
          )
        }
      />

      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <Button size="sm" variant="secondary" disabled={busy} aria-label="More actions">
            <MoreHorizontal aria-hidden className="size-3.5" />
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content align="end" side="top" sideOffset={6} className={MENU}>
            <DropdownMenu.Item
              className={ITEM}
              onSelect={() => run({ action: 'watch' }, (n) => `You’re watching ${pluralise(n, 'ticket')}`)}
            >
              <Eye aria-hidden className="size-3.5 text-ink-subtle" />
              Watch
            </DropdownMenu.Item>
            <DropdownMenu.Item
              className={ITEM}
              onSelect={() => run({ action: 'unwatch' }, (n) => `You’ve stopped watching ${pluralise(n, 'ticket')}`)}
            >
              <EyeOff aria-hidden className="size-3.5 text-ink-subtle" />
              Stop watching
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      {can('ticket.delete') && (
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => void deleteSelected()}
          className="text-critical-ink hover:bg-critical-soft hover:text-critical-ink"
        >
          <Trash2 aria-hidden className="size-3" />
          Delete
        </Button>
      )}

      <ClearButton onClear={onClear} disabled={busy} />

      {shownFailures && shownFailures.length > 0 && (
        <details className="basis-full text-mini text-ink-muted">
          <summary className="cursor-pointer text-caution-ink">
            Why {shownFailures.length === 1 ? 'one ticket was' : `${shownFailures.length} tickets were`} not changed
          </summary>
          <ul className="mt-1 max-h-32 space-y-0.5 overflow-y-auto">
            {shownFailures.slice(0, 20).map((failure) => (
              <li key={failure.number} dir="auto">
                #{failure.number} — {failure.reason}
              </li>
            ))}
            {shownFailures.length > 20 && <li>…and {shownFailures.length - 20} more.</li>}
          </ul>
        </details>
      )}
    </Floating>
  );
}

function Floating({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-3 z-20 flex justify-center px-3">
      <div
        role="region"
        aria-label={label}
        className="animate-rise pointer-events-auto flex max-w-full flex-wrap items-center gap-2 rounded-panel border border-line-strong bg-raised px-3 py-2 shadow-float"
      >
        {children}
      </div>
    </div>
  );
}

function ClearButton({ onClear, disabled }: { onClear: () => void; disabled?: boolean }) {
  return (
    <Button size="icon" variant="ghost" className="size-7" onClick={onClear} disabled={disabled} aria-label="Clear selection">
      <X aria-hidden className="size-3.5" />
    </Button>
  );
}

const MENU =
  'animate-pop scrollbar-slim z-50 max-h-72 min-w-44 overflow-y-auto rounded-card border border-line bg-raised p-1 shadow-float';
const ITEM =
  'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-body text-ink outline-none data-highlighted:bg-sunken';

/** A selection's identity regardless of the order the boxes were ticked in. */
function selectionKeyOf(numbers: number[]): string {
  return [...numbers].sort((a, b) => a - b).join(',');
}

/** Radix triggers hand their child a ref and handlers, so this forwards both. */
const MenuButton = forwardRef<HTMLButtonElement, ButtonProps & { label: string }>(function MenuButton(
  { label, ...props },
  ref,
) {
  return (
    <Button ref={ref} size="sm" variant="secondary" {...props}>
      {label}
      <ChevronDown aria-hidden className="size-3" />
    </Button>
  );
});

function ChoiceMenu({
  label,
  choices,
  disabled,
  onPick,
}: {
  label: string;
  choices: Choice[];
  disabled?: boolean;
  onPick: (choice: Choice) => void;
}) {
  if (choices.length === 0) return null;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <MenuButton label={label} disabled={disabled} />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="start" side="top" sideOffset={6} className={MENU}>
          {choices.map((choice) => (
            <DropdownMenu.Item key={choice.value} onSelect={() => onPick(choice)} className={ITEM}>
              {choice.label}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function GroupMenu({
  disabled,
  onPick,
}: {
  disabled?: boolean;
  onPick: (group: { id: number; name: string } | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const groups = useGroups(open);
  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <MenuButton label="Group" disabled={disabled} />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="start" side="top" sideOffset={6} className={MENU}>
          {groups.isLoading && <p className="px-2 py-1.5 text-mini text-ink-subtle">Loading groups…</p>}
          {groups.isError && (
            <p role="alert" className="px-2 py-1.5 text-mini text-critical-ink">
              Couldn’t load groups.
            </p>
          )}
          {(groups.data ?? []).map((group) => (
            <DropdownMenu.Item key={group.id} onSelect={() => onPick(group)} className={ITEM}>
              <span dir="auto">{group.name}</span>
            </DropdownMenu.Item>
          ))}
          {groups.data && (
            <>
              <DropdownMenu.Separator className="my-1 h-px bg-line" />
              <DropdownMenu.Item onSelect={() => onPick(null)} className={cn(ITEM, 'text-ink-muted')}>
                No group
              </DropdownMenu.Item>
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/**
 * Assign: yourself and nobody first, then anyone by name.
 *
 * Taking five unassigned tickets used to mean scrolling the agent list for
 * your own name, and there was no way at all to clear an assignee.
 */
function AssignMenu({
  disabled,
  meId,
  onAssign,
}: {
  disabled?: boolean;
  meId: number | null;
  onAssign: (person: { id: number; name: string } | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const agents = useAgents(open);
  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <MenuButton label="Assign" disabled={disabled} />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="start" side="top" sideOffset={6} className={cn(MENU, 'min-w-56')}>
          {meId !== null && (
            <DropdownMenu.Item className={ITEM} onSelect={() => onAssign({ id: meId, name: 'you' })}>
              <UserPlus aria-hidden className="size-3.5 text-accent" />
              Assign to me
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Item className={ITEM} onSelect={() => onAssign(null)}>
            <UserMinus aria-hidden className="size-3.5 text-ink-subtle" />
            Unassign
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="my-1 h-px bg-line" />
          <DropdownMenu.Label className="px-2 py-1 text-mini font-medium text-ink-subtle">Agents</DropdownMenu.Label>
          {agents.isLoading && <p className="px-2 py-1.5 text-mini text-ink-subtle">Loading agents…</p>}
          {agents.isError && (
            <p role="alert" className="px-2 py-1.5 text-mini text-critical-ink">
              Couldn’t load agents.
            </p>
          )}
          {(agents.data ?? [])
            .filter((agent) => agent.id !== meId)
            .map((agent) => (
              <DropdownMenu.Item
                key={agent.id}
                className={ITEM}
                onSelect={() => onAssign({ id: agent.id, name: agent.name })}
              >
                <Avatar name={agent.name} size="xs" />
                <span dir="auto" className="min-w-0 flex-1 truncate">
                  {agent.name}
                </span>
                {agent.is_available === false && <Badge>Away</Badge>}
              </DropdownMenu.Item>
            ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function TagsMenu({
  disabled,
  onApply,
}: {
  disabled?: boolean;
  onApply: (action: 'add_tags' | 'remove_tags', tags: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const tags = [
    ...new Set(
      draft
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean),
    ),
  ];
  const tooMany = tags.length > MAX_TAGS;
  const tooLong = tags.some((tag) => tag.length > 64);
  const problem = tooMany
    ? `At most ${MAX_TAGS} tags at a time.`
    : tooLong
      ? 'A tag can be at most 64 characters.'
      : null;
  const usable = tags.length > 0 && problem === null;

  const apply = (action: 'add_tags' | 'remove_tags') => {
    if (!usable) return;
    onApply(action, tags);
    setDraft('');
    setOpen(false);
  };
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button size="sm" variant="secondary" disabled={disabled} aria-expanded={open}>
          <Tag aria-hidden className="size-3" />
          Tags
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          side="top"
          sideOffset={6}
          className="animate-pop z-50 w-72 rounded-card border border-line bg-raised p-3 shadow-float"
        >
          <form
            onSubmit={(event) => {
              event.preventDefault();
              apply('add_tags');
            }}
          >
            <label htmlFor="bulk-tags" className="mb-1 block text-mini font-medium text-ink-muted">
              Tags, separated by commas
            </label>
            <Input
              id="bulk-tags"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="printer, floor-3"
              aria-invalid={problem ? true : undefined}
              aria-describedby={problem ? 'bulk-tags-error' : undefined}
              className="h-7 text-mini"
            />
            {problem && (
              <p id="bulk-tags-error" role="alert" className="mt-1 text-mini text-critical-ink">
                {problem}
              </p>
            )}
            <div className="mt-2 flex justify-end gap-1.5">
              <Button size="sm" variant="ghost" disabled={!usable} onClick={() => apply('remove_tags')}>
                Remove
              </Button>
              <Button size="sm" variant="primary" type="submit" disabled={!usable}>
                <Check aria-hidden className="size-3" />
                Add
              </Button>
            </div>
          </form>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
